import { Box, VStack } from "@chakra-ui/react";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { offeringIsCompleted } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types";
import { BannerAlert, Heading, Text } from "@equidefi/ui";
import type React from "react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import { ReinvestBox } from "@/components/investment/ReinvestBox";
import { useUserInvestments } from "@/hooks/useInvestments";
import { SectionButton } from "./InvestmentDetails/components";
import { VaultGroupCard } from "./PortfolioDashboard/VaultGroupCard";
import { VaultContainer } from "./components";

/**
 * OfferingInvestmentsView - Shows all investments for a specific offering
 * grouped by vault record
 */
const OfferingInvestmentsView: React.FC = () => {
  const { offeringId } = useParams<{ offeringId: string }>();
  const navigate = useNavigate();
  const { data: allInvestments, isLoading, error } = useUserInvestments({}, { refetchOnMount: "always" });

  // Filter investments for this offering
  const investments = useMemo(() => {
    if (!allInvestments || !offeringId) return [];
    return allInvestments.filter((inv) => inv.offering_id === offeringId);
  }, [allInvestments, offeringId]);

  const offering = investments[0]?.offering;
  const issuer = investments[0]?.issuer;

  useDocumentTitle([issuer?.name || "Investments", "Portfolio"]);

  // Check if offering is completed to determine if we should show reinvest box
  const isOfferingCompleted = offeringIsCompleted(offering);

  // Check if any investment has CANCELED status
  const hasNonCanceledInvestment = useMemo(() => {
    return investments.some((inv) => inv.status !== "CANCELED");
  }, [investments]);

  /**
   * Group investments by vault_id
   */
  const investmentsByVault = useMemo(() => {
    const grouped = new Map<string, DTO.Investment[]>();

    for (const investment of investments) {
      const vaultId = investment.vault_id || "unknown";
      if (!grouped.has(vaultId)) {
        grouped.set(vaultId, []);
      }
      const group = grouped.get(vaultId);
      if (group) {
        group.push(investment);
      }
    }

    return grouped;
  }, [investments]);

  if (isLoading) {
    return <VaultContainer />;
  }

  if (error) {
    return (
      <VaultContainer>
        <SectionButton to="/vault/dashboard" iconDirection="left">
          Back To Portfolio
        </SectionButton>
        <BannerAlert
          status="error"
          title="Failed to Load Investments"
          description="Please refresh the page or contact support if the problem persists."
        />
      </VaultContainer>
    );
  }

  if (investments.length === 0) {
    return (
      <VaultContainer>
        <SectionButton to="/vault/dashboard" iconDirection="left">
          Back To Portfolio
        </SectionButton>
        <Text mt="4">No investments found for this offering.</Text>
      </VaultContainer>
    );
  }

  return (
    <VaultContainer>
      <SectionButton to="/vault/dashboard" iconDirection="left">
        Back To Portfolio
      </SectionButton>

      <Box bg="white" borderRadius="lg" p="4" boxShadow="sm" border="1px" borderColor="purple.400" mb="4">
        <Heading fontWeight="bold" fontSize="xl" mb="2">
          Your investments in {issuer?.name || "this offering"}
        </Heading>
        <Text fontSize="md" color="gray.600" mb="0">
          Click on an investment to view more details.
        </Text>
      </Box>

      <VStack spacing="4" align="stretch">
        {Array.from(investmentsByVault.entries()).map(([vaultId, vaultInvestments]) => (
          <VaultGroupCard
            key={vaultId}
            investments={vaultInvestments}
            onInvestmentClick={(investmentId) => navigate(`/vault/investment/${investmentId}`)}
          />
        ))}
      </VStack>

      {!isOfferingCompleted && offering && hasNonCanceledInvestment && (
        <Box mt="4">
          <ReinvestBox offering={offering} />
        </Box>
      )}
    </VaultContainer>
  );
};

export default OfferingInvestmentsView;
